/**
 * 司机端 — 我的任务（当前任务卡片 + 轨迹上报状态）
 * N6: 列表走司机身份接口 GET /transit/driver/orders（仅本人名下运输单）。
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { fetchDriverOrders, reportGps } from '@/api/transit';
import { Empty, Loading, OrderStatusTag } from '@/components/common';
import type { TransitOrder } from '@/types';
import { timeAgo } from '@/utils/format';

export default function DriverHome() {
  const [orders, setOrders] = useState<TransitOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [reporting, setReporting] = useState(false);
  const [lastReport, setLastReport] = useState<string>(new Date().toISOString());

  useEffect(() => {
    fetchDriverOrders({ page_size: 100 })
      .then((d) => {
        setOrders(d.list);
        setLoading(false);
      })
      .catch(() => {
        setOrders([]);
        setLoading(false);
      });
  }, []);

  if (loading) return <Loading />;

  // 当前任务：运输中 > 待装车 > 已到达
  const active = orders.find((o) => o.status === 1) ?? orders.find((o) => o.status === 0) ?? orders.find((o) => o.status === 2);
  const others = orders.filter((o) => o.id !== active?.id);

  const simulateReport = async () => {
    if (!active) return;
    setReporting(true);
    try {
      const now = new Date();
      await reportGps([
        {
          transit_id: active.id,
          vehicle_id: active.vehicle_id,
          lng: active.last_lng + (Math.random() - 0.5) * 0.02,
          lat: active.last_lat + (Math.random() - 0.5) * 0.02,
          speed: 45 + Math.round(Math.random() * 30),
          gps_time: now.toISOString(),
        },
      ]);
      setLastReport(now.toISOString());
    } catch (e) {
      alert(e instanceof Error ? e.message : '位置上报失败，请稍后重试');
    } finally {
      setReporting(false);
    }
  };

  const online = Date.now() - new Date(lastReport).getTime() < 3 * 60000;

  return (
    <div className="driver-page">
      {/* 轨迹上报状态 */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-title">
          <span>📍 轨迹上报</span>
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              fontSize: 12,
              color: online ? 'var(--color-green-dark)' : 'var(--color-red)',
              fontWeight: 700,
            }}
          >
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: online ? 'var(--color-green)' : 'var(--color-red)' }} />
            {online ? '在线' : '离线'}
          </span>
        </div>
        <div style={{ fontSize: 13, color: 'var(--color-ink-secondary)', marginBottom: 12 }}>
          最近上报：{timeAgo(lastReport)}
        </div>
        <button className="btn btn-primary" disabled={!active || reporting} onClick={simulateReport} style={{ width: '100%' }}>
          {reporting ? '上报中…' : '模拟位置上报'}
        </button>
        <p style={{ fontSize: 12, color: 'var(--color-ink-muted)', marginTop: 8 }}>生产环境由司机 APP 自动上报（60s/次）；此处演示上报链路。</p>
      </div>

      {/* 当前任务 */}
      {active ? (
        <div className="card" style={{ marginBottom: 16, border: '2px solid var(--color-blue)' }}>
          <div className="card-title">
            <span>🚚 当前任务</span>
            <OrderStatusTag status={active.status} />
          </div>
          <div style={{ fontSize: 14, lineHeight: 2 }}>
            <div>
              <strong>{active.origin_city}</strong> → <strong>{active.destination_city}</strong>
            </div>
            <div style={{ fontFamily: 'monospace', fontSize: 12, color: 'var(--color-ink-secondary)' }}>{active.transit_no}</div>
            <div>牛只 {active.cattle_count} 头 · {active.vehicle?.plate_no ?? `车辆 #${active.vehicle_id}`}</div>
            <div>最近位置：{active.last_lng.toFixed(4)}, {active.last_lat.toFixed(4)} · {active.last_speed.toFixed(0)} km/h</div>
          </div>
          <Link className="btn btn-ghost" to={`/driver/orders/${active.id}`} style={{ width: '100%', marginTop: 12 }}>
            查看任务详情 →
          </Link>
        </div>
      ) : (
        <div className="card" style={{ marginBottom: 16 }}>
          <Empty text="暂无进行中的任务" />
        </div>
      )}

      {/* 其他任务 */}
      <div className="card">
        <div className="card-title">📋 任务列表</div>
        {others.length === 0 ? (
          <Empty text="暂无其他任务" />
        ) : (
          others.map((o) => (
            <Link key={o.id} to={`/driver/orders/${o.id}`}>
              <div className="row-between" style={{ padding: '10px 0', borderBottom: '1px solid var(--color-line)' }}>
                <div>
                  <div>
                    {o.origin_city} → {o.destination_city}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--color-ink-muted)' }}>{o.transit_no}</div>
                </div>
                <OrderStatusTag status={o.status} />
              </div>
            </Link>
          ))
        )}
      </div>
    </div>
  );
}
